import {
  collectDefaultsForFields,
  isFieldValueMissing,
  unwrapFieldValue,
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { isComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { FormLayoutItem } from 'features/libra/sections/formLayout'
import { Section } from 'features/libra/sections/sectionTypes'
import {
  normalizeComputedSectionFieldValues,
  restoreMappedOverrideField,
} from 'features/libra/stepComponents/conditionalForm/computed/evaluateComputedSection'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { applyStepBranchSetFields } from 'features/libra/workflow/stepBranching'
import { StepBranchConfig } from 'features/libra/workflow/workflowStepsApi'
import isEqual from 'lodash/isEqual'

export type DeriveStepFieldValuesInput = {
  fieldValues: FieldValues
  sections: ReadonlyArray<Section>
  form: ReadonlyArray<FormLayoutItem>
  ontologyFields: Record<string, OntologyField>
  branch: StepBranchConfig | undefined
}

export type DerivedStepFieldValues = {
  allVisibleSections: Section[]
  resolvedFieldValues: FieldValues
  fieldValuesToSave: FieldValues
}

/**
 * The pure pipeline behind a step's rendered and persisted field values:
 * visibility, defaults, computed sections, then branch `setFields`.
 */
export function deriveStepFieldValues({
  fieldValues,
  sections,
  form,
  ontologyFields,
  branch,
}: DeriveStepFieldValuesInput): DerivedStepFieldValues {
  const visibleSectionIds = getVisibleSectionIds(form, fieldValues)
  const allVisibleSections = visibleSectionIds
    .map((sectionId) => sections.find((section) => section.id === sectionId))
    .filter((section): section is Section => section !== undefined)

  const visibleFields = allVisibleSections
    .filter((section) => section.type === 'form')
    .flatMap((section) => section.fields)

  const defaultsForVisibleFields = collectDefaultsForFields(
    fieldValues,
    ontologyFields,
    visibleFields
  )

  const questionListDefaults = Object.fromEntries(
    allVisibleSections
      .flatMap((section) =>
        section.type === 'form' &&
        section.presentation?.variant === 'question-list'
          ? section.fields
          : []
      )
      .filter((fieldId) => {
        const field = ontologyFields[fieldId]
        return (
          field?.category === FieldCategory.INPUT && field.type === 'boolean'
        )
      })
      .filter((fieldId) =>
        isFieldValueMissing(unwrapFieldValue(fieldValues[fieldId]))
      )
      .map((fieldId) => [fieldId, wrapFieldValue(false)])
  )

  const fieldValuesWithDefaults = Object.fromEntries(
    Object.entries({
      ...fieldValues,
      ...defaultsForVisibleFields,
      ...questionListDefaults,
    }).filter(([, value]) => value !== undefined)
  ) as FieldValues

  const visibleComputedSections = allVisibleSections.filter(isComputedSection)
  const computedContext = { ontologyFields, sections }

  const resolvedFieldValues = visibleComputedSections.reduce(
    (values, section) =>
      normalizeComputedSectionFieldValues(section, values, computedContext),
    fieldValuesWithDefaults
  )

  const derivedFieldValues = visibleComputedSections.reduce(
    (values, section) =>
      restoreMappedOverrideField(
        section,
        resolvedFieldValues,
        values,
        computedContext
      ),
    applyStepBranchSetFields(branch, resolvedFieldValues)
  )

  const fieldValuesToSave = markDerivedAsSystem(fieldValues, derivedFieldValues)

  return {
    allVisibleSections,
    resolvedFieldValues,
    fieldValuesToSave,
  }
}

/**
 * Marks every field whose saved value was produced by this pipeline (computed
 * outputs, branch writes, ontology and question-list defaults) as `system`.
 * A field equal to what the user has locally keeps its own origin, so an
 * overridden computed field is not relabelled.
 */
function markDerivedAsSystem(
  localFieldValues: FieldValues,
  fieldValuesToSave: FieldValues
): FieldValues {
  return Object.fromEntries(
    Object.entries(fieldValuesToSave).map(([fieldId, wrapper]) => {
      const local = localFieldValues[fieldId]
      return [
        fieldId,
        local && isEqual(local.value, wrapper.value)
          ? wrapper
          : withFieldOrigin(wrapper, 'system'),
      ]
    })
  )
}
